import React, {useCallback, useEffect, useRef} from 'react';
import {usePanorama} from '@wix/yoshi-flow-editor';
import {FormViewer} from '@wix/form-viewer/widget';
import {Accordion, AccordionTheme, Divider} from 'wix-ui-tpa/cssVars';
import {FormModel} from '../../../../../../domain/models/checkout/Cart/Form.model';
import {classes} from './AdditionalDetailsStepOpen.st.css';
import {useICUI18n} from '../../../../Hooks/useICUI18n';
import {AdditionalDetailsFormSubtitle} from './AdditionalDetailsFormSubtitle';
import {useFormInstance} from '../../../../Form/useFormInstance';
import {useAdditionalDetailsData} from './WithAdditionalDetailsData';

export enum AdditionalDetailsFormDataHook {
  formItem = 'AdditionalDetailsForm.formItem',
  formItemAccordion = 'AdditionalDetailsForm.formItemAccordion',
  formItemContent = 'AdditionalDetailsForm.formItemContent',
}

export const AdditionalDetailsForm: React.FC<{form: FormModel}> = ({form}) => {
  const {id, formSchemaId, title, description} = form;
  const i18n = useICUI18n();
  const panorama = usePanorama();

  const {data: formInstance} = useFormInstance({});
  const {registerForm, unregisterForm} = useAdditionalDetailsData();

  const {formValues, formErrors, formRef, setFormValues, setFormErrors} = formInstance;
  const elementRef = useRef<HTMLLIElement>(null);

  const focusOnForm = useCallback(() => {
    elementRef.current?.scrollIntoView({behavior: 'smooth', block: 'center'});
  }, []);

  useEffect(
    () => {
      registerForm(id, {
        focusOnForm,
        formInstance,
      });

      return () => {
        unregisterForm(id);
      };
    },
    /* eslint-disable react-hooks/exhaustive-deps*/ [id, formRef]
  );

  return (
    <li ref={elementRef} className={classes.eventItem} data-hook={AdditionalDetailsFormDataHook.formItem}>
      <Accordion
        data-hook={AdditionalDetailsFormDataHook.formItemAccordion}
        theme={AccordionTheme.Box}
        initiallyExpanded={[id]}>
        <Accordion.Item
          id={id}
          key={id}
          title={title}
          subtitle={(<AdditionalDetailsFormSubtitle description={description} />) as unknown as any}>
          <div
            role="presentation"
            onKeyDown={(e) => e.stopPropagation()}
            className={classes.formItemContent}
            data-hook={AdditionalDetailsFormDataHook.formItemContent}>
            <Divider className={classes.divider} />
            <FormViewer
              i18n={i18n}
              ref={formRef}
              panorama={panorama}
              formId={formSchemaId}
              values={formValues}
              errors={formErrors}
              onChange={setFormValues}
              onValidate={setFormErrors}
            />
          </div>
        </Accordion.Item>
      </Accordion>
    </li>
  );
};
